import {
  CANVAS_HEIGHT as BANNER_HEIGHT,
  CANVAS_WIDTH as BANNER_WIDTH,
  YOUTUBE_MAX_FILE_BYTES,
} from '../components/youtube-banner-resizer/bannerGeometry';
import {
  YOUTUBE_CHANNEL_BANNER_LAST_VERIFIED,
  YOUTUBE_CHANNEL_BANNER_SOURCE_URL,
} from '../components/youtube-banner-resizer/youtubeChannelBannerSpecs';
import { isPublishable, PseoEntryBase, PseoFaq, Rect, SourceRef } from './pseo/types';

export type PlatformId = 'youtube' | 'instagram' | 'tiktok' | 'facebook' | 'linkedin' | 'x' | 'pinterest' | 'twitch';

export interface Spec {
  width: number;
  height: number;
  aspectRatio: string;
  minSize?: { width: number; height: number };
  maxFileSizeBytes?: number;
  formats: string[];
  safeArea?: Rect[];
  deviceCrops?: { device: 'tv' | 'desktop' | 'tablet' | 'mobile'; rect: Rect }[];
  notes?: string[];
}

export interface Asset extends PseoEntryBase {
  platform: PlatformId;
  name: string;
  spec: Spec;
  /** An existing URL owns this query → no page is generated; hubs link to `ownerPath`. */
  ownerPath?: string;
  /** Resizer / checker to promote from the page. */
  toolPath?: string;
  toolLabel?: string;
  /** H1 that answers the query in its first 60 characters. */
  h1: string;
  /** Visible plain-text answer under the H1. */
  answer: string;
  /** Show the free preview resizer (only when `spec` is an upload size, not a display size). */
  resizer: boolean;
}

export interface Platform {
  id: PlatformId;
  name: string;
  hubIntro: string;
  assets: Asset[];
}

const LAST_VERIFIED = '2026-10-16';
const MB = 1000 * 1000;

const yt = (path: string, label: string): SourceRef => ({
  label: `YouTube Help: ${label}`,
  url: `https://support.google.com/youtube/answer/${path}`,
});
const YT_BRANDING = yt('10456525', 'manage your channel branding');
const META_ADS = (path: string, label: string): SourceRef => ({
  label: `Meta Ads Guide: ${label}`,
  url: `https://www.facebook.com/business/ads-guide/update/${path}`,
});

const formatBytes = (bytes: number) => {
  if (bytes >= 1000 * MB) return `${bytes / (1000 * MB)} GB`;
  return bytes >= MB ? `${bytes / MB} MB` : `${bytes / 1000} KB`;
};

/** Generated FAQs keep the visible FAQ and JSON-LD in sync with the spec values. */
function specFaqs(platformName: string, asset: Omit<Asset, 'faqs'>, extra: PseoFaq[] = []): PseoFaq[] {
  const { spec } = asset;
  const faqs: PseoFaq[] = [
    { question: `What size is a ${platformName} ${asset.name.toLowerCase()}?`, answer: asset.answer },
  ];
  if (spec.formats.length > 0 || spec.maxFileSizeBytes) {
    const formats = spec.formats.length ? `Use ${spec.formats.join(', ')}.` : '';
    const size = spec.maxFileSizeBytes ? ` Keep the file under ${formatBytes(spec.maxFileSizeBytes)}.` : '';
    faqs.push({
      question: `What file type should a ${platformName} ${asset.name.toLowerCase()} be?`,
      answer: `${formats}${size}`.trim(),
    });
  }
  return [...faqs, ...extra];
}

type AssetInput = Omit<Asset, 'faqs' | 'lastVerified' | 'changelog' | 'status' | 'platform'> & {
  status?: Asset['status'];
  extraFaqs?: PseoFaq[];
  lastVerified?: string;
};

const PLATFORM_NAMES: Record<PlatformId, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  x: 'X (Twitter)',
  pinterest: 'Pinterest',
  twitch: 'Twitch',
};

const asset =
  (platform: PlatformId) =>
  ({ extraFaqs, status = 'verified', lastVerified = LAST_VERIFIED, ...input }: AssetInput): Asset => {
    const base = {
      ...input,
      platform,
      status,
      lastVerified,
      changelog: [
        {
          date: lastVerified,
          change: status === 'verified' ? 'Checked against official source.' : 'Draft: no official figure.',
        },
      ],
    };
    return { ...base, faqs: specFaqs(PLATFORM_NAMES[platform], base, extraFaqs) };
  };

const youtube = asset('youtube');
const instagram = asset('instagram');
const tiktok = asset('tiktok');
const facebook = asset('facebook');
const linkedin = asset('linkedin');
const x = asset('x');
const pinterest = asset('pinterest');
const twitch = asset('twitch');

const IG_FEED: SourceRef = {
  label: 'Instagram Help: photo sizes and aspect ratios',
  url: 'https://help.instagram.com/1631821640426723',
};
const FB_PAGE_IMAGES: SourceRef = {
  label: 'Facebook Help: Page profile and cover photo sizes',
  url: 'https://www.facebook.com/help/125379114252045',
};

export const SOCIAL_PLATFORMS: Platform[] = [
  {
    id: 'youtube',
    name: 'YouTube',
    hubIntro:
      'YouTube image and video sizes from YouTube Help: thumbnails, profile picture, videos, Shorts, end screens, watermark and channel banner.',
    assets: [
      youtube({
        slug: 'thumbnail',
        primaryQuery: 'youtube thumbnail size',
        name: 'Thumbnail',
        h1: 'YouTube Thumbnail Size: 3840×2160 px (16:9)',
        answer:
          'YouTube recommends a 16:9 thumbnail up to 3840×2160 px, at least 640 px wide, as a JPG or PNG. 1280×720 still works; it is above the minimum.',
        spec: {
          width: 3840,
          height: 2160,
          aspectRatio: '16:9',
          minSize: { width: 640, height: 360 },
          maxFileSizeBytes: 50 * MB,
          formats: ['JPG', 'PNG'],
          notes: [
            'Upload limit is 2 MB from the mobile app and 50 MB from a computer.',
            'Shorts thumbnails are 9:16, up to 2160×3840 px.',
            'Vertical videos with a 16:9 custom thumbnail get an automatic 4:5 version on Home and Subscriptions.',
          ],
        },
        sourceUrls: [yt('72431', 'add video thumbnails')],
        toolPath: '/free-tools/youtube-thumbnail-resizer',
        toolLabel: 'Open the YouTube thumbnail resizer',
        resizer: true,
      }),
      youtube({
        slug: 'profile-picture',
        primaryQuery: 'youtube profile picture size',
        name: 'Profile picture',
        h1: 'YouTube Profile Picture Size: 98×98 px (Square)',
        answer:
          'Upload a square JPG, GIF, BMP or PNG up to 15 MB. YouTube displays the profile picture at 98×98 px, cropped to a circle; it does not publish a recommended upload size.',
        spec: {
          width: 98,
          height: 98,
          aspectRatio: '1:1',
          maxFileSizeBytes: 15 * MB,
          formats: ['JPG', 'GIF (not animated)', 'BMP', 'PNG'],
          notes: [
            '98×98 px is the display size. Commonly quoted upload sizes such as 800×800 are not in YouTube Help.',
          ],
        },
        sourceUrls: [YT_BRANDING],
        resizer: false,
      }),
      youtube({
        slug: 'video',
        primaryQuery: 'youtube video size',
        name: 'Video',
        h1: 'YouTube Video Size: 1920×1080 px (16:9)',
        answer:
          'YouTube videos are 16:9. Upload 1920×1080 for HD, or any of 3840×2160, 2560×1440, 1280×720 and the other 16:9 sizes YouTube lists. Other ratios are padded by the player.',
        spec: {
          width: 1920,
          height: 1080,
          aspectRatio: '16:9',
          formats: ['MP4 (H.264, AAC) recommended'],
          notes: [
            '16:9 resolutions listed by YouTube: 7680×4320, 3840×2160, 2560×1440, 1920×1080, 1280×720, 854×480, 640×360, 426×240.',
            'Do not add black bars; YouTube pads non-16:9 video automatically.',
          ],
        },
        sourceUrls: [
          yt('6375112', 'video resolution and aspect ratios'),
          yt('1722171', 'recommended upload encoding settings'),
        ],
        toolPath: '/free-tools/video-file-size-calculator',
        toolLabel: 'Estimate the file size',
        resizer: false,
      }),
      youtube({
        slug: 'shorts',
        primaryQuery: 'youtube shorts size',
        name: 'Shorts',
        h1: 'YouTube Shorts Size: 1080×1920 px (9:16)',
        answer:
          'A Short needs a vertical or square aspect ratio and can be up to 3 minutes long. Google recommends 1080×1920 px (9:16) for vertical video, with 720×1280 as the minimum.',
        spec: {
          width: 1080,
          height: 1920,
          aspectRatio: '9:16',
          minSize: { width: 720, height: 1280 },
          formats: ['MP4 (H.264, AAC) recommended'],
          notes: [
            'YouTube Help gives the ratio and length; the pixel size comes from Google Ads Help for vertical video.',
          ],
        },
        sourceUrls: [
          yt('12779649', 'upload YouTube Shorts'),
          {
            label: 'Google Ads Help: vertical video specs',
            url: 'https://support.google.com/google-ads/answer/13547298',
          },
        ],
        toolPath: '/safe-zone-checker/youtube-safe-zone-checker',
        toolLabel: 'Check the Shorts safe zone',
        resizer: true,
      }),
      youtube({
        slug: 'end-screen',
        primaryQuery: 'youtube end screen size',
        name: 'End screen',
        h1: 'YouTube End Screen Size: 5–20 s, Up to 4 Elements',
        answer:
          'End screens appear in the last 5 to 20 seconds of a video that is at least 25 seconds long. Add up to 4 elements on 16:9 videos; custom images should be at least 300×300 px.',
        spec: {
          width: 300,
          height: 300,
          aspectRatio: '1:1',
          formats: [],
          notes: [
            'Element types: video or playlist, subscribe, channel, and link (link needs YouTube Partner Program).',
            'Non-16:9 videos may allow fewer elements.',
          ],
        },
        sourceUrls: [yt('6388789', 'add end screens to videos')],
        resizer: true,
      }),
      youtube({
        slug: 'watermark',
        primaryQuery: 'youtube watermark size',
        name: 'Watermark',
        h1: 'YouTube Watermark Size: 150×150 px Minimum (1:1)',
        answer:
          'The video watermark (branding) image must be square, at least 150×150 px and under 1 MB. It is not shown on videos made for kids.',
        spec: {
          width: 150,
          height: 150,
          aspectRatio: '1:1',
          maxFileSizeBytes: 1 * MB,
          formats: [],
          notes: ['Not clickable on mobile.'],
        },
        sourceUrls: [YT_BRANDING],
        resizer: true,
      }),
      youtube({
        slug: 'channel-banner',
        primaryQuery: 'youtube banner size',
        name: 'Channel banner',
        h1: 'YouTube Banner Size: 2560×1440 px',
        answer: 'YouTube channel banners are 2560×1440 px (16:9), at least 2048×1152 px and 6 MB or smaller.',
        spec: {
          width: BANNER_WIDTH,
          height: BANNER_HEIGHT,
          aspectRatio: '16:9',
          minSize: { width: 2048, height: 1152 },
          maxFileSizeBytes: YOUTUBE_MAX_FILE_BYTES,
          formats: ['JPG', 'PNG', 'GIF', 'BMP'],
        },
        sourceUrls: [{ label: 'YouTube Help: manage your channel branding', url: YOUTUBE_CHANNEL_BANNER_SOURCE_URL }],
        lastVerified: YOUTUBE_CHANNEL_BANNER_LAST_VERIFIED,
        ownerPath: '/free-tools/youtube-banner-resizer',
        resizer: false,
      }),
    ],
  },
  {
    id: 'instagram',
    name: 'Instagram',
    hubIntro: 'Instagram post, story, reel and carousel sizes from Instagram Help and Meta’s Ads Guide.',
    assets: [
      instagram({
        slug: 'post',
        primaryQuery: 'instagram post size',
        name: 'Post',
        h1: 'Instagram Post Size: 1080×1440 px (3:4) or Wider',
        answer:
          'Instagram keeps feed photos 1080 px wide at any aspect ratio between 1.91:1 and 3:4, so posts range from 1080×566 to 1080×1440 px. Upload at least 1080 px wide.',
        spec: {
          width: 1080,
          height: 1440,
          aspectRatio: '3:4 (tallest)',
          minSize: { width: 320, height: 168 },
          formats: ['JPG', 'PNG'],
          notes: [
            'Photos outside 1.91:1 to 3:4 are cropped to fit.',
            'Meta’s Ads Guide still recommends 4:5 (1440×1800) for feed ads.',
            'No Instagram Help page documents the profile grid showing 3:4 thumbnails.',
          ],
        },
        sourceUrls: [IG_FEED, META_ADS('image/instagram-feed', 'Instagram feed image')],
        resizer: true,
      }),
      instagram({
        slug: 'story',
        primaryQuery: 'instagram story size',
        name: 'Story',
        h1: 'Instagram Story Size: 1080×1920 px (9:16)',
        answer:
          'Instagram Stories are vertical 9:16. Meta recommends 1080×1920 px and keeping about 14% of the top, 35% of the bottom and 6% of each side free of text.',
        spec: {
          width: 1080,
          height: 1920,
          aspectRatio: '9:16',
          maxFileSizeBytes: 30 * MB,
          formats: ['JPG', 'PNG'],
          notes: [
            'Figures come from Meta’s Stories ads guidance; organic Stories use the same full-screen canvas.',
            'The Ads Guide also accepts 1440×2560 uploads.',
          ],
        },
        sourceUrls: [
          {
            label: 'Meta Business Help: design requirements for Instagram Stories ads',
            url: 'https://www.facebook.com/business/help/2222978001316177',
          },
          META_ADS('image/instagram-story', 'Instagram Stories image'),
        ],
        toolPath: '/safe-zone-checker/instagram-story-safe-zone-checker',
        toolLabel: 'Check the Story safe zone',
        resizer: true,
      }),
      instagram({
        slug: 'reel',
        primaryQuery: 'instagram reel size',
        name: 'Reel',
        h1: 'Instagram Reel Size: 1080×1920 px (9:16)',
        answer:
          'Instagram Reels accept 1.91:1 to 9:16 at 720 px or more and at least 30 fps. Full-screen vertical Reels are 9:16, such as 1080×1920 px. The cover photo is 420×654 px.',
        spec: {
          width: 1080,
          height: 1920,
          aspectRatio: '9:16',
          minSize: { width: 720, height: 1280 },
          formats: ['MP4', 'MOV'],
          notes: ['Meta’s Ads Guide recommends uploading Reels ads at 1440×2560.', 'Cover photo: 420×654 px (1:1.55).'],
        },
        sourceUrls: [
          { label: 'Instagram Help: Reel size and aspect ratio', url: 'https://help.instagram.com/1038071743007909' },
          META_ADS('video/instagram-reels', 'Instagram Reels video'),
        ],
        toolPath: '/safe-zone-checker/instagram-safe-zone-checker',
        toolLabel: 'Check the Reels safe zone',
        resizer: true,
      }),
      instagram({
        slug: 'carousel',
        primaryQuery: 'instagram carousel size',
        name: 'Carousel',
        h1: 'Instagram Carousel Size: 1080×1440 px (3:4)',
        answer:
          'A carousel holds up to 20 photos and videos, and the orientation of the first item (square, portrait or landscape) applies to all of them. Use feed sizes: 1080 px wide, between 1.91:1 and 3:4.',
        spec: {
          width: 1080,
          height: 1440,
          aspectRatio: '3:4 (tallest)',
          formats: [],
          notes: ['Up to 20 items per post.'],
        },
        sourceUrls: [
          {
            label: 'Instagram Help: share multiple photos or videos',
            url: 'https://help.instagram.com/269314186824048',
          },
          IG_FEED,
        ],
        resizer: true,
      }),
      instagram({
        slug: 'profile-picture',
        primaryQuery: 'instagram profile picture size',
        name: 'Profile picture',
        h1: 'Instagram Profile Picture Size',
        answer: 'Instagram Help does not publish a profile picture size.',
        spec: { width: 320, height: 320, aspectRatio: '1:1', formats: [] },
        sourceUrls: [],
        status: 'draft',
        resizer: false,
      }),
    ],
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    hubIntro: 'TikTok video sizes from TikTok’s developer and ads documentation.',
    assets: [
      tiktok({
        slug: 'video',
        primaryQuery: 'tiktok video size',
        name: 'Video',
        h1: 'TikTok Video Size: 9:16, 540×960 px Minimum',
        answer:
          'TikTok recommends vertical 9:16 video at 540×960 px or larger. Uploads can be MP4, MOV or WebM, 360 to 4096 px on each side and up to 4 GB.',
        spec: {
          width: 540,
          height: 960,
          aspectRatio: '9:16',
          maxFileSizeBytes: 4000 * MB,
          formats: ['MP4', 'MOV', 'WebM'],
          notes: [
            '540×960 is the minimum recommended by TikTok Ads; many creators export 1080×1920.',
            'All creators can post 3-minute videos; some can post up to 10 minutes.',
          ],
        },
        sourceUrls: [
          {
            label: 'TikTok Ads Help: video ads specifications',
            url: 'https://ads.tiktok.com/help/article/video-ads-specifications',
          },
          {
            label: 'TikTok for Developers: media transfer guide',
            url: 'https://developers.tiktok.com/doc/content-posting-api-media-transfer-guide',
          },
        ],
        toolPath: '/safe-zone-checker/tiktok-safe-zone-checker',
        toolLabel: 'Check the TikTok safe zone',
        resizer: true,
      }),
      tiktok({
        slug: 'profile-picture',
        primaryQuery: 'tiktok profile picture size',
        name: 'Profile picture',
        h1: 'TikTok Profile Picture Size',
        answer: 'TikTok only states a 20×20 px minimum.',
        spec: { width: 20, height: 20, aspectRatio: '1:1', formats: [] },
        sourceUrls: [
          {
            label: 'TikTok Support: profile photo',
            url: 'https://www.tiktok.com/support/faq_detail?id=7581821549855038008',
          },
        ],
        status: 'draft',
        resizer: false,
      }),
      tiktok({
        slug: 'photo-carousel',
        primaryQuery: 'tiktok photo carousel size',
        name: 'Photo carousel',
        h1: 'TikTok Photo Carousel Size',
        answer: 'TikTok allows up to 35 photos but publishes no pixel size or ratio.',
        spec: { width: 1080, height: 1920, aspectRatio: '9:16', formats: [] },
        sourceUrls: [],
        status: 'draft',
        resizer: false,
      }),
    ],
  },
  {
    id: 'facebook',
    name: 'Facebook',
    hubIntro: 'Facebook Page images from Facebook Help, and feed and Story sizes from Meta’s Ads Guide.',
    assets: [
      facebook({
        slug: 'cover-photo',
        primaryQuery: 'facebook cover photo size',
        name: 'Cover photo',
        h1: 'Facebook Cover Photo Size: 851×315 px',
        answer:
          'Facebook Page cover photos load fastest as an 851×315 px sRGB JPG under 100 KB, and must be at least 400×150 px. Desktop shows them at 16:9 and phones at 2.4:1.',
        spec: {
          width: 851,
          height: 315,
          aspectRatio: '2.7:1',
          minSize: { width: 400, height: 150 },
          formats: ['JPG (sRGB)', 'PNG for logos or text'],
          notes: [
            'Facebook says covers load fastest as an sRGB JPG under 100 KB.',
            'Facebook Help lists 16:9 (desktop) and 2.4:1 (mobile) display ratios alongside the 851×315 recommendation, which is about 2.7:1. Keep key content centered.',
            'Personal profile covers only need to be at least 720 px wide.',
          ],
        },
        sourceUrls: [
          FB_PAGE_IMAGES,
          { label: 'Facebook Help: profile cover photo', url: 'https://www.facebook.com/help/220070894714080' },
        ],
        resizer: true,
      }),
      facebook({
        slug: 'profile-picture',
        primaryQuery: 'facebook profile picture size',
        name: 'Profile picture',
        h1: 'Facebook Profile Picture Size: 320×320 px',
        answer:
          'Upload a 320×320 px Page profile picture. Facebook crops it to a circle and shows it at 176×176 px on computers and 196×196 px on smartphones.',
        spec: { width: 320, height: 320, aspectRatio: '1:1', formats: ['JPG', 'PNG for logos or text'] },
        sourceUrls: [FB_PAGE_IMAGES],
        resizer: true,
      }),
      facebook({
        slug: 'post',
        primaryQuery: 'facebook post size',
        name: 'Post image',
        h1: 'Facebook Post Image Size: 1440×1800 px (4:5)',
        answer: 'Meta recommends 4:5 feed images at 1440×1800 px, at least 600 px wide, as a JPG or PNG up to 30 MB.',
        spec: {
          width: 1440,
          height: 1800,
          aspectRatio: '4:5',
          minSize: { width: 600, height: 750 },
          maxFileSizeBytes: 30 * MB,
          formats: ['JPG', 'PNG'],
          notes: ['From Meta’s Ads Guide for Facebook feed images; Facebook Help has no separate organic post size.'],
        },
        sourceUrls: [META_ADS('image/facebook-feed', 'Facebook feed image')],
        resizer: true,
      }),
      facebook({
        slug: 'story',
        primaryQuery: 'facebook story size',
        name: 'Story',
        h1: 'Facebook Story Size: 1440×2560 px (9:16)',
        answer:
          'Meta recommends 9:16 Facebook Stories at 1440×2560 px, at least 500 px wide, up to 30 MB, with about 14% top, 35% bottom and 6% sides kept free of text.',
        spec: {
          width: 1440,
          height: 2560,
          aspectRatio: '9:16',
          minSize: { width: 500, height: 889 },
          maxFileSizeBytes: 30 * MB,
          formats: ['JPG', 'PNG'],
        },
        sourceUrls: [META_ADS('image/facebook-story', 'Facebook Stories image')],
        toolPath: '/safe-zone-checker/meta-ads-safe-zone-checker',
        toolLabel: 'Check the Stories safe zone',
        resizer: true,
      }),
      facebook({
        slug: 'event-cover',
        primaryQuery: 'facebook event cover size',
        name: 'Event cover',
        h1: 'Facebook Event Cover Size',
        answer: 'Facebook Help publishes no event cover size.',
        spec: { width: 1200, height: 628, aspectRatio: '1.91:1', formats: [] },
        sourceUrls: [],
        status: 'draft',
        resizer: false,
      }),
    ],
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    hubIntro: 'LinkedIn profile, company page and post image sizes from LinkedIn Help.',
    assets: [
      linkedin({
        slug: 'profile-banner',
        primaryQuery: 'linkedin banner size',
        name: 'Profile banner',
        h1: 'LinkedIn Banner Size: 1584×396 px (4:1)',
        answer:
          'LinkedIn recommends a 1584×396 px (4:1) background photo for personal profiles, as a JPG or PNG under 8 MB.',
        spec: {
          width: 1584,
          height: 396,
          aspectRatio: '4:1',
          maxFileSizeBytes: 8 * MB,
          formats: ['JPG', 'PNG'],
          notes: ['How it crops depends on window size and screen resolution.'],
        },
        sourceUrls: [
          {
            label: 'LinkedIn Help: add a background photo',
            url: 'https://www.linkedin.com/help/linkedin/answer/a568217',
          },
        ],
        resizer: true,
      }),
      linkedin({
        slug: 'company-cover',
        primaryQuery: 'linkedin company cover size',
        name: 'Company cover',
        h1: 'LinkedIn Company Cover Size: 1512×256 px',
        answer:
          'LinkedIn Page cover images are 1512×256 px (minimum and recommended), PNG or JPEG, up to 3 MB. The logo is 400×400 px.',
        spec: {
          width: 1512,
          height: 256,
          aspectRatio: '5.9:1',
          minSize: { width: 1512, height: 256 },
          maxFileSizeBytes: 3 * MB,
          formats: ['PNG', 'JPEG'],
          notes: [
            'The older 1128×191 size is no longer in LinkedIn Help.',
            'LinkedIn may trim the cover, so keep important details centered.',
          ],
        },
        sourceUrls: [
          {
            label: 'LinkedIn Help: Page image specifications',
            url: 'https://www.linkedin.com/help/linkedin/answer/a563309',
          },
        ],
        resizer: true,
      }),
      linkedin({
        slug: 'post-image',
        primaryQuery: 'linkedin post image size',
        name: 'Post image',
        h1: 'LinkedIn Post Image Size: 1200×627 px (1.91:1)',
        answer:
          'Use 1.91:1 images such as 1200×627 px for link posts. LinkedIn ads also accept 1:1 (1200×1200) and 4:5 (720×900) images up to 5 MB.',
        spec: {
          width: 1200,
          height: 627,
          aspectRatio: '1.91:1',
          maxFileSizeBytes: 5 * MB,
          formats: ['JPG', 'PNG', 'GIF'],
          notes: ['LinkedIn’s Page help says 1200×627; its ads specs say 1200×628.'],
        },
        sourceUrls: [
          {
            label: 'LinkedIn Help: Page image specifications',
            url: 'https://www.linkedin.com/help/linkedin/answer/a563309',
          },
          { label: 'LinkedIn Help: single image ad specs', url: 'https://www.linkedin.com/help/lms/answer/a426534' },
        ],
        resizer: true,
      }),
    ],
  },
  {
    id: 'x',
    name: 'X (Twitter)',
    hubIntro: 'X (Twitter) header and image sizes from X Help.',
    assets: [
      x({
        slug: 'header',
        primaryQuery: 'twitter header size',
        name: 'Header',
        h1: 'X (Twitter) Header Size: 1500×500 px (3:1)',
        answer:
          'X recommends a 1500×500 px (3:1) header. About 60 px at the top and bottom may be cropped depending on the screen.',
        spec: {
          width: 1500,
          height: 500,
          aspectRatio: '3:1',
          formats: [],
          notes: ['Profile photo: 400×400 px, JPEG, GIF or PNG, up to 2 MB.'],
        },
        sourceUrls: [
          {
            label: 'X Help: profile and header photo issues',
            url: 'https://help.x.com/en/managing-your-account/common-issues-when-uploading-profile-photo',
          },
        ],
        resizer: true,
      }),
      x({
        slug: 'post-image',
        primaryQuery: 'twitter image size',
        name: 'Post image',
        h1: 'X (Twitter) Post Image Size',
        answer: 'X shows single photos between 2:1 and 3:4 uncropped but publishes no pixel size.',
        spec: { width: 1200, height: 675, aspectRatio: '16:9', formats: [] },
        sourceUrls: [],
        status: 'draft',
        resizer: false,
      }),
    ],
  },
  {
    id: 'pinterest',
    name: 'Pinterest',
    hubIntro: 'Pinterest Pin sizes from Pinterest Help.',
    assets: [
      pinterest({
        slug: 'pin',
        primaryQuery: 'pinterest pin size',
        name: 'Pin',
        h1: 'Pinterest Pin Size: 1000×1500 px (2:3)',
        answer:
          'Pinterest recommends 2:3 Pins at 1000×1500 px, PNG or JPEG, up to 20 MB on desktop. Taller Pins may be cut off in the feed.',
        spec: {
          width: 1000,
          height: 1500,
          aspectRatio: '2:3',
          maxFileSizeBytes: 20 * MB,
          formats: ['PNG', 'JPEG'],
          notes: ['Up to 32 MB from the Pinterest app.', 'Full-bleed 9:16 video Pins have their own safe zone.'],
        },
        sourceUrls: [
          {
            label: 'Pinterest Business Help: product specs',
            url: 'https://help.pinterest.com/en/business/article/pinterest-product-specs',
          },
          { label: 'Pinterest Help: review Pin specs', url: 'https://help.pinterest.com/en/article/review-pin-specs' },
        ],
        toolPath: '/safe-zone-checker/pinterest-video-pin-safe-zone-checker',
        toolLabel: 'Check the video Pin safe zone',
        resizer: true,
      }),
    ],
  },
  {
    id: 'twitch',
    name: 'Twitch',
    hubIntro: 'Twitch channel page image sizes from Twitch Help.',
    assets: [
      twitch({
        slug: 'banner',
        primaryQuery: 'twitch banner size',
        name: 'Profile banner',
        h1: 'Twitch Banner Size: 1200×480 px',
        answer:
          'Twitch recommends a 1200×480 px profile banner. It is scaled to 480 px tall and stretched on wide windows, so keep graphics on the left.',
        spec: {
          width: 1200,
          height: 480,
          aspectRatio: '2.5:1',
          formats: [],
          notes: ['Profile picture: JPEG, PNG or GIF up to 10 MB.'],
        },
        sourceUrls: [
          { label: 'Twitch Help: channel page setup', url: 'https://help.twitch.tv/s/article/channel-page-setup' },
        ],
        resizer: true,
      }),
      twitch({
        slug: 'panel',
        primaryQuery: 'twitch panel size',
        name: 'Panel',
        h1: 'Twitch Panel Size: 320×300 px Maximum',
        answer:
          'Twitch panel images can be up to 320 px wide and 300 px tall and under 2.9 MB; larger images are scaled down automatically.',
        spec: { width: 320, height: 300, aspectRatio: '16:15', maxFileSizeBytes: 2.9 * MB, formats: [] },
        sourceUrls: [
          { label: 'Twitch Help: channel page setup', url: 'https://help.twitch.tv/s/article/channel-page-setup' },
        ],
        resizer: true,
      }),
      twitch({
        slug: 'offline-screen',
        primaryQuery: 'twitch offline screen size',
        name: 'Offline screen',
        h1: 'Twitch Offline Screen Size',
        answer: 'Twitch publishes no size for the video player banner.',
        spec: { width: 1920, height: 1080, aspectRatio: '16:9', formats: [] },
        sourceUrls: [],
        status: 'draft',
        resizer: false,
      }),
    ],
  },
];

export const SIZES_HUB_PATH = '/sizes';

export const platformPath = (platform: PlatformId) => `${SIZES_HUB_PATH}/${platform}`;

/** Where an asset lives: its owner page when one exists, otherwise its generated leaf. */
export const assetPath = (item: Asset) => item.ownerPath ?? `${SIZES_HUB_PATH}/${item.platform}/${item.slug}`;

/** Published assets: verified with a source. Draft entries never render or get linked. */
export const getPublishedAssets = (platform?: Platform) =>
  (platform ? platform.assets : SOCIAL_PLATFORMS.flatMap((item) => item.assets)).filter(isPublishable);

/** Assets that get a generated `/sizes/[platform]/[asset]` page. */
export const getGeneratedAssets = () => getPublishedAssets().filter((item) => !item.ownerPath);

/** Platforms with at least one published asset. */
export const getPublishedPlatforms = () =>
  SOCIAL_PLATFORMS.filter((platform) => getPublishedAssets(platform).length > 0);

export const getPlatform = (id: string) => getPublishedPlatforms().find((platform) => platform.id === id);

export const getAsset = (platformId: string, slug: string) =>
  getGeneratedAssets().find((item) => item.platform === platformId && item.slug === slug);

export const formatSpecSize = (spec: Spec) =>
  `${spec.width.toLocaleString('en-US')}×${spec.height.toLocaleString('en-US')} px`;

export { formatBytes as formatSpecFileSize, PLATFORM_NAMES };
